/**
 * HTTP client.
 *
 * Three behaviours here are worth more than the code that implements them:
 *
 * 1. The access token lives in a module variable, not localStorage. Anything
 *    readable by script is readable by injected script; the refresh token is an
 *    HttpOnly cookie the SPA cannot see at all.
 *
 * 2. Refresh is single-flight. A dashboard fires several requests at once, and
 *    if each one refreshed independently on a 401 they would rotate the refresh
 *    token concurrently. The backend treats a replayed refresh token as theft
 *    and revokes the whole family, so a naive retry would log the user out
 *    every time their token expired.
 *
 * 3. A 403 STEP_UP_REQUIRED is not a failure to display. The client asks the
 *    registered handler to re-authenticate and retries the original request
 *    once, so callers of dangerous endpoints do not each implement the flow.
 */

import { ApiError, NetworkError } from '@/lib/errors';
import type { ApiErrorBody, Envelope } from './types';

const BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string) || '/api/v1';

type StepUpHandler = () => Promise<string | null>;
type UnauthenticatedHandler = () => void;

let accessToken: string | null = null;
let stepUpToken: string | null = null;
let stepUpExpiresAt = 0;
let refreshInFlight: Promise<boolean> | null = null;
let onStepUpRequired: StepUpHandler | null = null;
let onUnauthenticated: UnauthenticatedHandler | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setStepUpToken(token: string | null, expiresInSeconds = 300): void {
  stepUpToken = token;
  stepUpExpiresAt = token ? Date.now() + expiresInSeconds * 1000 : 0;
}

export function hasValidStepUp(now = Date.now()): boolean {
  return Boolean(stepUpToken) && now < stepUpExpiresAt;
}

export function registerStepUpHandler(handler: StepUpHandler | null): void {
  onStepUpRequired = handler;
}

export function registerUnauthenticatedHandler(handler: UnauthenticatedHandler | null): void {
  onUnauthenticated = handler;
}

export function resetClientState(): void {
  accessToken = null;
  stepUpToken = null;
  stepUpExpiresAt = 0;
  refreshInFlight = null;
}

function correlationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip refresh-and-retry. Used by the auth endpoints themselves. */
  skipAuthRetry?: boolean;
  signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody['error'];
  try {
    body = ((await response.json()) as ApiErrorBody).error;
  } catch {
    body = { code: 'UNEXPECTED_RESPONSE', message: `Request failed (${response.status}).` };
  }
  return new ApiError(response.status, body);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId(),
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (hasValidStepUp() && stepUpToken) headers['X-Step-Up-Token'] = stepUpToken;

  try {
    return await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch {
    throw new NetworkError();
  }
}

/** Refresh the access token. Concurrent callers share one attempt. */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await send('/auth/refresh', { method: 'POST', skipAuthRetry: true });
      if (!response.ok) {
        accessToken = null;
        return false;
      }
      const body = (await response.json()) as Envelope<{ access_token: string }>;
      accessToken = body.data.access_token;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so simultaneous callers all observe the same
      // promise rather than starting a second refresh.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (response.status === 401 && !options.skipAuthRetry) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      onUnauthenticated?.();
      throw await parseError(response);
    }
    response = await send(path, options);
  }

  if (response.status === 403 && !options.skipAuthRetry) {
    const error = await parseError(response);
    if (error.needsStepUp && onStepUpRequired) {
      const token = await onStepUpRequired();
      if (!token) throw error;
      response = await send(path, options);
    } else {
      throw error;
    }
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const body = (await response.json()) as Envelope<T>;
  return body.data;
}

/**
 * A file rather than JSON, with the same authentication and refresh as
 * `request`. Returns the body and the name the server chose for it.
 */
export async function download(path: string): Promise<{ blob: Blob; filename: string }> {
  let response = await send(path, {});
  if (response.status === 401) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      onUnauthenticated?.();
      throw await parseError(response);
    }
    response = await send(path, {});
  }
  if (!response.ok) throw await parseError(response);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await response.blob(), filename: match?.[1] ?? 'report.csv' };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
